import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import humanFormat from 'human-format';
import Lottie from 'react-lottie';
import defaultOnErrorHandler from '../../shared/default-on-error-handler';
import * as likeAnimation from './like-animation.json';
import { useIsLarge } from '../../shared/theme';

export default function Like({
  likeable, like, unlike, returnToSection,
}) {
  React.useEffect(() => {
    const likeContainers = document.querySelectorAll('div.like-container');
    likeContainers.forEach(i => {
      i.addEventListener('keyup', e => {
        if (e.key === 'Tab') {
          i.classList.remove('no-focus-outline');
        }
      });
    });
  });

  const [initialCount, setInitialCount] = React.useState(likeable.likeCount);
  const [updatedCount, setUpdatedCount] = React.useState(likeable.likeCount);
  const [liked, setLiked] = React.useState(likeable.likedByUser);
  const [runIconAnimation, setRunIconAnimation] = React.useState(false);
  const [runCountAnimation, setRunCountAnimation] = React.useState(false);

  const lottieSize = useIsLarge() ? 32 : 40;
  const isOldSite = true;

  const toggle = e => {
    e && e.preventDefault();

    if (!window.Livewire.currentUser) {
      window.location = `/sign_up?return_to_section=${returnToSection}`;
      return;
    }

    if (!liked) {
      setRunIconAnimation(true);
    }

    (liked ? unlike : like)(likeable.id, isOldSite)
      .then(result => {
        setLiked(!liked);
        setUpdatedCount(result.data.like_count);
        if (liked) { setInitialCount(result.data.like_count); }
      })
      .catch(error => defaultOnErrorHandler(error, 'Cannot toggle like status'));
  };

  const keyPressed = e => {
    if (e.key === 'Enter') {
      toggle();
    }
  };

  const switchToCountAnimation = () => {
    setRunIconAnimation(false);
    if (initialCount > 0) {
      setRunCountAnimation(true);
      setTimeout(() => {
        setRunCountAnimation(false);
        setInitialCount(updatedCount);
      }, 200);
    } else {
      setInitialCount(updatedCount);
    }
  };

  const title = `${liked ? 'Unlike' : 'Like'} this ${likeable.type}`;
  const iconCls = {
    'icon-lw-liked': liked,
    'icon-lw-like': !liked,
  };

  const animationOptions = {
    loop: false,
    autoplay: false,
    animationData: likeAnimation.default,
    rendererSettings: {
      perserveAspectRatio: 'xMidYMid slice',
      progressiveLoad: true,
    },
  };

  return (
    <div className="like-container no-focus-outline" onClick={toggle} onKeyPress={keyPressed} data-id="like-link" title={title} role="button" tabIndex={0}>
      {!!updatedCount && (
      <div className={`count-container ${runCountAnimation ? 'count-animation' : ''}`}>
        {(!!runIconAnimation || !!runCountAnimation) && initialCount > 0 && (
        <span className="count initial-value">
          {' '}
          {humanFormat(initialCount, { decimals: 1 })}
          {' '}
        </span>
        )}
        <span className="count">{humanFormat(updatedCount, { decimals: 1 })}</span>
      </div>
      )}
      <div className="icon-container">
        {!runIconAnimation && <span className={classNames('icon-lw', iconCls)} />}
        {!!runIconAnimation && <Lottie options={animationOptions} className="like-animation" width={lottieSize} height={lottieSize} isClickToPauseDisabled eventListeners={[{ eventName: 'complete', callback: switchToCountAnimation }]} />}
      </div>
    </div>
  );
}

Like.propTypes = {
  likeable: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    likedByUser: PropTypes.bool.isRequired,
    likeCount: PropTypes.number.isRequired,
  }).isRequired,
  like: PropTypes.func.isRequired,
  unlike: PropTypes.func.isRequired,
  returnToSection: PropTypes.string.isRequired,
};
